import { getConfigTenant, isCorpTenant } from "@capone/common";
import { type CfarRateQuoteSuccess } from "@b2bportal/lodging-api";
import clsx from "clsx";
import {
  ActionButton,
  GenericInfoPopup,
  Icon,
  IconName,
  paymentTextConstants,
  useDeviceTypes,
} from "halifax";
import queryStringParser from "query-string";
import React, { useContext, useEffect, useState } from "react";
import { RouteComponentProps } from "react-router";
import {
  CloseButtonActionEnum,
  getSubtitleTrackingValue,
  MODAL_ALERT_CHOICE,
  ModalAlertProperties,
  ModalButtonType,
  ModalCategoryType,
  ModalScreens,
  type RoomInfoProducts,
  type RoomProduct,
} from "redmond";
import { CfarOfferType } from "redmond/apis/tysons/ancillaries";
import { ClientContext } from "../../../../App";
import { config } from "../../../../api/config";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import {
  CONTACT_SUPPORT_URL,
  PATH_AVAILABILITY,
  PATH_SHOP,
} from "../../../../utils/paths";
import {
  transformToStringifiedAvailabilityQuery,
  transformToStringifiedQuery,
} from "../../../shop/utils/queryStringHelpers";
import {
  ACCEPT_NEW_PRICE,
  ADD_LOYALTY_NUMBER,
  BACK_TO_SEARCH,
  CHANGE_HOTEL,
  CHANGE_SELECTION,
  CHOOSE_ANOTHER_HOTEL,
  CHOOSE_ANOTHER_RATE,
  CONFIRM_AND_BOOK,
  CONTACT_SUPPORT,
  CONTINUE,
  CORP_ERROR_ICON,
  EDIT_PAYMENT_INFO,
  ERROR_ICON,
  MODIFY_TRAVELERS,
  PRICE_DECREASE_ICON,
  PRICE_INCREASE_ICON,
  SEARCH_AGAIN,
  START_NEW_SEARCH,
  TRY_AGAIN,
  UNABLED_ICON,
  UPDATE_PAYMENT_INFO,
  VIEW_ALL_ROOMS,
  VIEW_OTHER_OPTIONS,
  WARNING_TRIANGLE_ICON,
} from "../../reducer/selectors/textConstants";
import { AGENT_FEE } from "../index";
import { BookingErrorModalConnectorProps } from "./container";
import "./styles.scss";
import dayjs from "dayjs";
import { CaponeEmbeddedProvider, EmbeddedRoomSelection } from "@capone/portal";
import "@capone/portal/style.css";
import {
  AVAILABLE,
  getExperimentVariantCustomVariants,
  SHOP_ON_CHECKOUT_ERRORS_UX_EXPERIMENT,
  SHOP_ON_CHECKOUT_ERRORS_UX_VARIANTS,
  useExperiments,
} from "../../../../context/experiments";

export interface IBookingErrorModalProps
  extends BookingErrorModalConnectorProps,
    RouteComponentProps {}

// Declared locally: @capone/portal's index.d.ts re-exports
// EmbeddedRoomSelectionProps from a cross-repo path, so importing it yields
// `unknown`.
interface EmbeddedRoomSelectionResult {
  room?: RoomProduct;
  roomInfo?: RoomInfoProducts;
  cfarOffer?: CfarRateQuoteSuccess;
}

interface EmbeddedRoomsLoadedResult {
  isEmpty: boolean;
}

export const BookingErrorModal = ({
  hasError,
  errorTitles,
  isUnavailableError,
  history,
  scheduleBook,
  lodging,
  fromDate,
  untilDate,
  adultsCount,
  children,
  hotelQueryParams,
  isBookingValid,
  acknowledgePriceDifference,
  resetBookErrors,
  modalType,
  priceQuoteSuccessful,
  roomsCount,
  petsCount,
  ancillaries,
  step,
  hotelCfarDetailsAtPriceQuote,
  lodgingSelection,
  chosenRoomInfo,
  chosenProduct,
  cancellationSummary,
  hasAttemptedReshop,
  markReshopAttempted,
  schedulePriceQuote,
  markConnectionRetryPending,
  isConnectionError,
  setHotelShop,
  setSelectedCfarId,
  setHasSelectedRefundableRoom,
}: IBookingErrorModalProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasNoAlternativeRooms, setHasNoAlternativeRooms] = useState(false);
  const { matchesMobile } = useDeviceTypes();
  const clientContext = useContext(ClientContext);
  const { isAgentPortal } = clientContext;
  const isCorporate = isCorpTenant(getConfigTenant());

  const expState = useExperiments();
  const isShopOnCheckoutErrorsUxEnabled =
    getExperimentVariantCustomVariants(
      expState.experiments,
      SHOP_ON_CHECKOUT_ERRORS_UX_EXPERIMENT,
      SHOP_ON_CHECKOUT_ERRORS_UX_VARIANTS
    ) === AVAILABLE;

  const lodgingId = lodging?.lodging.id;
  const showRoomReselection =
    isShopOnCheckoutErrorsUxEnabled &&
    isUnavailableError &&
    !hasAttemptedReshop &&
    lodgingId != null &&
    fromDate != null &&
    untilDate != null;

  const { primaryButtonText, secondaryButtonText } = errorTitles;

  const modalEventProperties: ModalAlertProperties = {
    lob: "hotel",
    type: modalType,
    screen: ModalScreens.HOTELS_CHECKOUT,
    primary_button: primaryButtonText!,
    secondary_button: secondaryButtonText!,
    category: ModalCategoryType.TROUBLE,
    modal_subtitle: getSubtitleTrackingValue(errorTitles),
    modal_title: errorTitles.title,
    agent_title: errorTitles.agentTitle!,
    agent_subtitle: errorTitles.agentSubtitle!,
    funnel: "hotels",
    step: step!,
    ...(showRoomReselection || (isUnavailableError && hasAttemptedReshop)
      ? { is_reshop: true }
      : {}),
    ...([
      "hotel_with_add_on_price_difference",
      "hotel_price_difference",
    ].includes(modalType)
      ? hotelCfarDetailsAtPriceQuote
      : {}),
  };

  useEffect(() => {
    setIsOpen(hasError);
    setHasNoAlternativeRooms(false);
  }, [hasError]);

  const redoBook = () => {
    const queryString = queryStringParser.parse(history.location.search);
    scheduleBook({
      agentFee: isAgentPortal ? AGENT_FEE : 0,
      isRecommended: queryString.recommended === "true",
      ancillaries: ancillaries,
      initialTime: dayjs().toISOString(),
    });
  };

  const redirectToShop = () => {
    const queryString = queryStringParser.parse(history.location.search);
    const isTravelApp = queryString.isTravelApp === "true";

    history.push(
      `${PATH_SHOP}${transformToStringifiedQuery({
        lodgingId: lodging!.lodging.id,
        ...hotelQueryParams,
        roomsCount,
        lodgingSelection,
        isTravelApp,
      })}`,
      { fromPage: location.pathname }
    );
  };

  const redirectToAvailability = () => {
    const queryString = queryStringParser.parse(history.location.search);
    const isTravelApp = queryString.isTravelApp === "true";

    const search = transformToStringifiedAvailabilityQuery(
      lodging!.lodging.city,
      fromDate,
      untilDate,
      adultsCount,
      children,
      roomsCount,
      petsCount,
      undefined,
      isTravelApp
    );
    history.push(`${PATH_AVAILABILITY}${search}`);
  };

  const contactSupport = () =>
    window.open(CONTACT_SUPPORT_URL, "_blank", "noopener")?.focus();

  // setHotelShop must land before schedulePriceQuote — the saga builds its
  // request from state. pollQuoteOnly is required: without it a matching total
  // makes the poll saga auto-dispatch scheduleBook and charge for an
  // unconfirmed room.
  const handleAlternativeRoomSelected = (
    selection: EmbeddedRoomSelectionResult
  ) => {
    const { room, roomInfo, cfarOffer } = selection;
    if (room == null || roomInfo == null || lodging == null) return;

    const productIndex = roomInfo.products.findIndex(
      (product) => product.rateId?.value === room.rateId?.value
    );
    if (productIndex < 0) return;

    setIsOpen(false);
    markReshopAttempted();
    setHotelShop({
      selectedLodging: lodging,
      roomInfoProducts: [roomInfo],
      selectedRoomId: roomInfo.roomInfo.roomId,
      chosenRoomInfoIndex: 0,
      chosenProductIndex: productIndex,
      chosenRateId: room.rateId?.value,
      hotelShopLodgingSelection: lodgingSelection,
      cancellationSummary,
    });

    // priceQuoteParamsSelector routes Hts -> encryptedHtsCfarOfferToken and
    // Legacy -> encryptedCfarId, so a wrong mapping silently drops the CFAR.
    setSelectedCfarId(
      cfarOffer != null
        ? {
            value: cfarOffer.id,
            offerType:
              cfarOffer.offerType.CfarOfferType === "Hts"
                ? CfarOfferType.Hts
                : CfarOfferType.Legacy,
          }
        : null
    );
    // "RoomsList" placement means coverage is bundled into the room row, so the
    // standalone upsell is suppressed. Not the same as "has a CFAR offer".
    setHasSelectedRefundableRoom(
      cfarOffer?.merchandisingPlacement?.MerchandisingPlacement === "RoomsList"
    );

    resetBookErrors();
    schedulePriceQuote({ history, pollQuoteOnly: true });
  };

  const handleRoomsLoaded = ({ isEmpty }: EmbeddedRoomsLoadedResult) => {
    setHasNoAlternativeRooms(isEmpty);
  };

  const reshopFooterLabels = {
    primary: BACK_TO_SEARCH,
    secondary: hasNoAlternativeRooms ? undefined : VIEW_ALL_ROOMS,
  };

  const scrollToTravelers = () => {
    const travelerSectionElement = document.querySelector(
      ".traveler-select-workflow-container"
    );

    const BANNER_HEIGHT =
      document
        .getElementById("b2bportal-banner-container")
        ?.getBoundingClientRect().height ?? 0;
    const HEADER_HEIGHT = matchesMobile
      ? 0
      : document.querySelector(".app-header")?.getBoundingClientRect().height ??
        0;
    const MARGIN = matchesMobile ? 18 : 20;

    const yOffset =
      window.pageYOffset - (BANNER_HEIGHT + HEADER_HEIGHT + MARGIN);

    const y =
      (travelerSectionElement?.getBoundingClientRect().top ?? 0) + yOffset;

    window.scrollTo({ top: y, behavior: "smooth" });
  };

  const handleButtonClick = (
    actionKey: string,
    isPrimary = false,
    buttonLabels?: { primary: string; secondary?: string }
  ) => {
    setIsOpen(false);
    trackEvent({
      eventName: MODAL_ALERT_CHOICE,
      properties: {
        ...modalEventProperties,
        ...(buttonLabels != null
          ? {
              primary_button: buttonLabels.primary,
              secondary_button: buttonLabels.secondary,
            }
          : {}),
        button_choice: isPrimary
          ? ModalButtonType.PRIMARY
          : ModalButtonType.SECONDARY,
      },
    });

    switch (actionKey) {
      case SEARCH_AGAIN:
        resetBookErrors();
        redirectToShop();
        break;
      case TRY_AGAIN:
        acknowledgePriceDifference();
        if (isConnectionError) {
          // Fresh quote on a new session; pollQuoteOnly stops before auto-book so
          // the customer confirms again. Sold-out requote → Unavailable modal.
          markConnectionRetryPending();
          resetBookErrors();
          schedulePriceQuote({ history, pollQuoteOnly: true });
        } else if (isBookingValid && priceQuoteSuccessful) {
          resetBookErrors();
          redoBook();
        } else {
          redirectToShop();
        }
        break;
      case CHOOSE_ANOTHER_RATE:
      case VIEW_ALL_ROOMS:
        redirectToShop();
        break;
      case VIEW_OTHER_OPTIONS:
        redirectToShop();
        break;
      case START_NEW_SEARCH:
        redirectToAvailability();
        break;
      case CHANGE_HOTEL:
      case CHOOSE_ANOTHER_HOTEL:
      case CHANGE_SELECTION:
      case BACK_TO_SEARCH:
        redirectToAvailability();
        break;
      case UPDATE_PAYMENT_INFO:
      case CONTINUE:
      case ACCEPT_NEW_PRICE:
        acknowledgePriceDifference();
        break;
      case CONFIRM_AND_BOOK:
        acknowledgePriceDifference();
        redoBook();
        break;
      case MODIFY_TRAVELERS:
        resetBookErrors();
        scrollToTravelers();
        break;
      case EDIT_PAYMENT_INFO:
      case paymentTextConstants.ADD_NEW_PAYMENT_METHOD:
        resetBookErrors(true);
        break;
      case CONTACT_SUPPORT:
        contactSupport();
        break;
      case ADD_LOYALTY_NUMBER:
        setIsOpen(false);
        setTimeout(() => resetBookErrors(), 500);
        break;
      default:
        break;
    }
  };

  const isPaymentError =
    primaryButtonText === paymentTextConstants.ADD_NEW_PAYMENT_METHOD ||
    primaryButtonText === EDIT_PAYMENT_INFO;

  const contextfulButtons = (() => {
    const disableFallback = isPaymentError && isCorpTenant(config.TENANT);
    // Not `secondaryButtonText == null` — several error states omit it and rely
    // on the SEARCH_AGAIN fallback below.
    const singleButtonOnly = isUnavailableError && hasAttemptedReshop;
    // buttons intentionally saved in reverse order for rendering
    const defaultButtons = [
      (!disableFallback || secondaryButtonText) && !singleButtonOnly
        ? {
            buttonText: secondaryButtonText || SEARCH_AGAIN,
            defaultStyle: "h4r-secondary",
            onClick: () =>
              handleButtonClick(secondaryButtonText || SEARCH_AGAIN),
          }
        : [],
      !disableFallback || primaryButtonText
        ? {
            buttonText: primaryButtonText || CHOOSE_ANOTHER_HOTEL,
            defaultStyle: "h4r-primary",
            onClick: () =>
              handleButtonClick(
                primaryButtonText || CHOOSE_ANOTHER_HOTEL,
                true
              ),
          }
        : [],
    ].flat();

    return defaultButtons;
  })();

  const getIconImage = (type: string) => {
    const iconMap: { [key: string]: JSX.Element } = {
      [ERROR_ICON]: isCorpTenant(config.TENANT) ? (
        <Icon className="error-icon" name={IconName.Caution} />
      ) : (
        <Icon className="error-icon" name={IconName.ErrorState} />
      ),
      [UNABLED_ICON]: (
        <Icon className="error-icon" name={IconName.UnableToProcess} />
      ),
      [PRICE_DECREASE_ICON]: (
        <Icon className="error-icon" name={IconName.ModalPriceDrop} />
      ),
      [PRICE_INCREASE_ICON]: (
        <Icon className="error-icon" name={IconName.PriceIncrease} />
      ),
      [CORP_ERROR_ICON]: (
        <Icon className="error-icon" name={IconName.ErrorTriangle} />
      ),
      [WARNING_TRIANGLE_ICON]: (
        <Icon className="error-icon" name={IconName.Caution} />
      ),
    };

    return iconMap[type] || iconMap[ERROR_ICON];
  };

  const closeButtonAction = (closeButtonAction?: CloseButtonActionEnum) => {
    const close =
      closeButtonAction === CloseButtonActionEnum.HoteLoyaltyClose
        ? () => {
            setIsOpen(false);
            setTimeout(() => resetBookErrors(), 500);
          }
        : () => {
            setIsOpen(false);
          };

    // Ignore backdrop clicks while the reshop embed is shown so the user can't
    // dismiss it mid-reselection (MUI passes reason="backdropClick"); the close
    // button and other actions invoke this with no reason and still close.
    return (_event?: unknown, reason?: string) => {
      if (showRoomReselection && reason === "backdropClick") return;
      close();
    };
  };

  return (
    <GenericInfoPopup
      agentSubtitle={errorTitles.agentSubtitle}
      agentTitle={errorTitles.agentTitle}
      buttons={showRoomReselection ? [] : contextfulButtons}
      className={clsx("booking-error-modal", errorTitles.type, {
        mobile: matchesMobile,
        noIconPadding: isCorporate && errorTitles.icon === ERROR_ICON,
        reshop: showRoomReselection,
      })}
      image={getIconImage(errorTitles.icon as string)}
      isAgent={isAgentPortal}
      isMobile={matchesMobile}
      open={isOpen}
      subtitle={errorTitles.subtitle}
      title={errorTitles.title}
      // note: mobile popovers in the checkout workflow have z-index: 1300
      zIndex={matchesMobile ? 1301 : 1300}
      showCloseButton={errorTitles.showCloseButton}
      onClose={closeButtonAction(errorTitles.closeButtonAction)}
      // The embed manages its own focus; relax MUI's focus trap.
      disableEnforceFocus={showRoomReselection}
    >
      {showRoomReselection &&
        lodgingId != null &&
        fromDate != null &&
        untilDate != null && (
          <CaponeEmbeddedProvider>
            <EmbeddedRoomSelection
              lodgingId={lodgingId}
              fromDate={dayjs(fromDate).format("YYYY-MM-DD")}
              untilDate={dayjs(untilDate).format("YYYY-MM-DD")}
              adultsCount={adultsCount}
              childrenAges={children}
              roomsCount={roomsCount}
              unavailableSelection={{
                room: chosenProduct ?? undefined,
                roomInfo: chosenRoomInfo ?? undefined,
              }}
              onRoomSelected={handleAlternativeRoomSelected}
              onRoomsLoaded={handleRoomsLoaded}
            />
          </CaponeEmbeddedProvider>
        )}

      {/*
        Children, not `buttons`: GenericInfoPopup renders buttons before
        children. Labels are passed explicitly because the error's own button
        copy is suppressed in this state.
      */}
      {showRoomReselection && (
        <div className="reshop-footer">
          <ActionButton
            className="reshop-footer-tertiary"
            defaultStyle="h4r-secondary"
            message={BACK_TO_SEARCH}
            onClick={() =>
              handleButtonClick(BACK_TO_SEARCH, true, reshopFooterLabels)
            }
          />
          {!hasNoAlternativeRooms && (
            <ActionButton
              className="reshop-footer-secondary"
              defaultStyle="h4r-secondary"
              message={VIEW_ALL_ROOMS}
              onClick={() =>
                handleButtonClick(VIEW_ALL_ROOMS, false, reshopFooterLabels)
              }
            />
          )}
        </div>
      )}
    </GenericInfoPopup>
  );
};
